import { AIJobProgress } from "./compassAITypes";
import { CompassError } from "./errorTypes";

export const PILLARS_V2 = [
  "INTENT",
  "REACH",
  "APPROACH",
  "PERSISTENCE",
  "RESONANCE",
  "INFLUENCE",
] as const;
export type PillarV2 = (typeof PILLARS_V2)[number];

export const ASSESSMENT_STATES_V2 = [
  "STRONG",
  "ADEQUATE",
  "MIXED",
  "WEAK",
  "NOT_RATABLE",
] as const;
export type AssessmentStateV2 = (typeof ASSESSMENT_STATES_V2)[number];

export const SINS_V2 = [
  "IRRELEVANCE",
  "OPAQUE_OBJECTIVES",
  "MISATTRIBUTION",
  "STALE_ENGAGEMENTS",
  "UNVERIFIABLE_OUTCOMES",
  "MIXED_SIGNALS",
  "NO_STRATEGIC_CONTEXT",
] as const;
export type SinV2 = (typeof SINS_V2)[number];

export const CONFIDENCES_V2 = ["HIGH", "MEDIUM", "LOW"] as const;
export type ConfidenceV2 = (typeof CONFIDENCES_V2)[number];

export type EvidenceRefV2 = string; // opaque id/URL — resolver handles linking

export type PillarResultV2 = {
  pillar: PillarV2;
  state: AssessmentStateV2;
  judgement: string;
  evidence_refs: EvidenceRefV2[];
  confidence: ConfidenceV2;
};

export type V2SinResult = {
  sin: SinV2;
  judgement: string;
  evidence_refs: EvidenceRefV2[];
  confidence: ConfidenceV2;
};

export type ExpectationV2 = {
  expectation: string;
  evidence_refs: EvidenceRefV2[];
};

export type EngagementAssessmentV2 = {
  pillars: PillarResultV2[];
  sins: V2SinResult[];
  expectations: ExpectationV2[];
};

export type EngagementAssessmentV2Run = {
  assessed_at: string; // ISO-8601
  assessment: EngagementAssessmentV2;
  prompt_version: string;
  model_version: string;
};

export type EngagementAssessmentV2HistoryResponse = {
  engagement_record_id: string;
  runs: EngagementAssessmentV2Run[]; // ordered desc by assessed_at
};

export type AssessEngagementV2Request = {
  engagement_record_id: string;
};

export type BatchAssessEngagementV2Request = {
  engagement_record_ids?: string[];
  manager_id?: string[];
  compass_cid?: string[];
};

export type BatchAssessmentV2Result = {
  jobId: string;
  successfulIds: string[];
  failedIds: string[];
};

export type GetEngagementAssessmentV2Request = {
  engagement_record_id: string;
};

export type JobStateV2<TResult> =
  | { kind: "idle" }
  | { kind: "queued"; jobId: string }
  | { kind: "running"; jobId: string; progress: AIJobProgress }
  | { kind: "success"; jobId: string; result: TResult }
  | {
      kind: "failed";
      jobId: string;
      error: CompassError | Error;
      message: string;
    }
  | { kind: "timeout"; jobId: string; elapsedMs: number }
  | { kind: "cancelled_locally"; jobId: string };

export const isTerminal = <T>(s: JobStateV2<T>): boolean =>
  s.kind === "success" ||
  s.kind === "failed" ||
  s.kind === "timeout" ||
  s.kind === "cancelled_locally";

export type ManagerId = string;
export type ThemeId = string;

export type DashboardFilters = {
  managerIds: ManagerId[]; // [] = unconstrained
  themes: ThemeId[]; // [] = unconstrained
  dateRange: { start: string; end: string }; // ISO-8601, inclusive
};

export type LatestRunPerEngagement = {
  engagement_record_id: string;
  manager_id: ManagerId;
  manager_name: string;
  themes: ThemeId[];
  assessed_at: string;
  assessment: EngagementAssessmentV2;
};

export type HeatmapCell<Axis extends string> = {
  row: ManagerId; // stable id, display name resolved at render
  col: Axis;
  value: number; // NaN when count === 0
  count: number;
};

export type ManagerPillarHeatmapData = {
  managers: { id: ManagerId; name: string }[];
  cells: HeatmapCell<PillarV2>[];
};

export type ManagerSinHeatmapData = {
  managers: { id: ManagerId; name: string }[];
  cells: HeatmapCell<SinV2>[];
};

export type ScatterPoint = {
  unitId: string; // engagement_record_id (see §4.4 for unit choice)
  unitLabel: string;
  x: number; // pillar metric
  y: number; // sin metric
  count: number;
};

export type PillarsSinsScatterData = {
  points: ScatterPoint[];
};
